import { NullableClassName } from '@squareup/dex-types-shared-ui';
import { ThemeSwitcher } from '@squareup/dex-ui';
import { Box } from '@squareup/dex-ui-shared-base';
import { Grid, GridItemRenderProps } from '@squareup/dex-ui-shared-grid';
import { TrackedBox } from '@squareup/dex-ui-user-interaction-publisher';
import clsx from 'clsx';
import React, { ElementType, ReactElement, ReactNode } from 'react';

import pageStyles from './page-layout.module.css';

export interface PageLayoutProps {
  contents: ReactElement;
  Navigation?: ElementType;
  Header: ElementType;
  Footer: ElementType;
  apiExplorerVariant?: boolean;
  themeSwitcher?: ReactNode;
}

const PageLayout = ({
  contents,
  Header,
  Navigation,
  Footer,
  className,
  apiExplorerVariant = false,
  themeSwitcher,
}: PageLayoutProps & NullableClassName) => {
  const render = ({ styles: gridStyles }: GridItemRenderProps) => {
    return (
      <>
        <TrackedBox
          className={clsx(
            gridStyles['grid-item-full'],
            apiExplorerVariant && pageStyles['api-explorer-variant'],
            pageStyles['top-layout']
          )}
          trackingId="header"
        >
          <Header />
          {themeSwitcher && (
            <Box className={pageStyles['theme-switcher']}>{themeSwitcher}</Box>
          )}
        </TrackedBox>
        {Navigation && (
          <TrackedBox
            className={clsx(
              gridStyles['grid-item-small'],
              pageStyles['nav-layout']
            )}
            trackingId="navigation"
          >
            <Navigation />
            <ThemeSwitcher
              className={clsx(
                pageStyles['page-layout-theme-switcher'],
                pageStyles['theme-switcher']
              )}
            />
          </TrackedBox>
        )}
        <TrackedBox
          className={clsx(
            pageStyles['page-main'],
            !apiExplorerVariant && pageStyles['min-height'],
            apiExplorerVariant
              ? gridStyles['grid-item-full']
              : gridStyles['grid-item-medium']
          )}
          trackingId="main"
        >
          {contents}
        </TrackedBox>
        <TrackedBox
          className={clsx(
            gridStyles['grid-item-full'],
            apiExplorerVariant && pageStyles['no-border']
          )}
          trackingId="footer"
        >
          <Footer
            /* The footer duplicates classes which is not a good pattern. So to override we have to do the same :( */
            footerClassName={clsx(
              pageStyles['footer'],
              pageStyles['page-footer'],
              pageStyles['layout-footer'],
              pageStyles['page-layout-footer']
            )}
          />
        </TrackedBox>
      </>
    );
  };

  return (
    <Grid
      className={clsx(
        pageStyles['page-layout'],
        apiExplorerVariant && pageStyles['api-explorer-layout'],
        className
      )}
      variant="app"
      render={render}
    />
  );
};

export { PageLayout };
